import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Connection, PublicKey, Transaction, VersionedMessage } from "@solana/web3.js";
import { getAssociatedTokenAddress, createTransferInstruction } from "@solana/spl-token";
import { SolflareWalletAdapter } from "@solana/wallet-adapter-solflare";
import {
  HeroContainer,
  HeroWrap,
  TextContent,
  TitleContainer,
  BusinessTitle,
  MainTitle,
  RaisedContainer,
  RaisedText,
  PurchaseTitle,
  CountdownContainer,
  CountdownItem,
  CountdownNumber,
  CountdownLabel,
  TimeNotice,
  TimeNoticePill,
  TimeNoticeText,
  PriceText,
  ButtonsContainer,
  PurchaseButton,
  ButtonIcon,
  MediaContainer,
  MediaTitle,
  LogoSlider,
  LogoContainer,
  LogoImage,
  BackgroundOverlay,
  ModalOverlay,
  ModalContent,
  CloseButton,
  ModalTitle,
  InputGroup,
  InputLabel,
  Input,
  SubmitButton,
  SuccessMessage,
  SuccessIcon,
  SuccessText,
  SuccessDescription,
  ErrorMessage,
  PriceInfo,
  PriceRow,
  PaymentMethod,
  PaymentOption,
  ImageContainer,
  ImageWrapper,
  HeroImage,
  InfoContainer,
  TopWrapper,
  BottomWrapper,
  NumberWrapper,
  TimeProgressBar,
  TimeProgressFill,
} from "../styles/sections/HeroSectionStyles";

const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
const finalText = "Titan's tap";

// 개발용
// const SOLANA_CONNECTION = "https://api.devnet.solana.com" // 솔라나 데브(테스트)넷 연결 (HTTP 사용)
// const USDT_TOKEN_ADDRESS = "873bvV2UrnzuHXJVfEpiuc2LV5tjN79ZW37WqAc83LSY";
// const SELLER_WALLET_ADDRESS = "HKjiMuJXSqwWiyLJg9GruN2uzmEg4nn6iJJGv2FwbiWb"; // 판매자 지갑 주소 - 구매자의 토큰이 들어옴

// 실제 운영용
const SOLANA_CONNECTION = "https://api.mainnet-beta.solana.com"; // 솔라나 메인넷 연결 (HTTP 사용)
const USDC_TOKEN_ADDRESS = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v";
const SELLER_WALLET_ADDRESS = "7RiDntuVVGtU63cqnsfJbDWvzT4pQ4WtgUADPNiTb3vE"; // 판매자 지갑 주소 - 구매자의 토큰이 들어옴
const TITANIUM_TOKEN_MINT = "ecc8kZyhVojbuhUoqRGuPjfvEjrR4Y5dRQ86V3aP7aD"; // TITANIUM 토큰 mint 주소

const TITANIUM_PRICE = 0.0079; // 토큰 가격
const MIN_AMOUNT = 100; // 최소 구매 금액

const API_URL = "https://api.titanstap.io";

function HeroSection() {
  // 타겟 시간 설정 (2025년 6월 16일 UTC 0시)
  const targetDate = new Date('2025-06-16T00:00:00Z');
  
  const calculateTimeLeft = () => {
    const now = new Date();
    const difference = targetDate - now;
    
    // 이미 지난 날짜인 경우 0으로 설정
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    
    // 남은 시간 계산
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / (1000 * 60)) % 60);
    const seconds = Math.floor((difference / 1000) % 60);
    
    return { days, hours, minutes, seconds };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [prevTimeLeft, setPrevTimeLeft] = useState(calculateTimeLeft());

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [progress, setProgress] = useState(100);
  const [wallet, setWallet] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [provider, setProvider] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);

  const logos = [
    {
      src: "https://framerusercontent.com/images/rwIuQQHBrb3UCGKD9qkn6jJZ4Y.png",
      alt: "CoinDesk",
    },
    {
      src: "https://framerusercontent.com/images/Z8rbVSx1g9L6lgXRZ9BBxIzb7AA.png",
      alt: "CoinTelegraph",
    },
    {
      src: "https://framerusercontent.com/images/MLP7ugSKbp9PnEVgFfSkRFTzYo.png",
      alt: "CryptoNews",
    },
  ];

  // 로고 배열을 5번 복제하여 무한 슬라이드 효과 구현
  const duplicatedLogos = [...logos, ...logos, ...logos, ...logos, ...logos];

  const mainTitleRef = useRef(null);
  const sectionRef = useRef(null);
  const frameRef = useRef();
  const iterationRef = useRef(0);
  const lengthRef = useRef(1);
  const isAnimatingRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);    
  const [key, setKey] = useState(0); // 애니메이션 리셋을 위한 key

  const scrambleText = () => {
    if (!mainTitleRef.current || !isAnimatingRef.current) return;

    let result = "";
    let complete = true;
    const currentLength = Math.min(lengthRef.current, finalText.length);

    for (let i = 0; i < currentLength; i++) {
      if (i < iterationRef.current) {
        result += finalText[i];
      } else {
        complete = false;
        result += chars[Math.floor(Math.random() * chars.length)];
      }
    }

    mainTitleRef.current.textContent = result;

    if (!complete) {
      iterationRef.current++;
      frameRef.current = requestAnimationFrame(scrambleText);
    } else if (lengthRef.current < finalText.length) {
      lengthRef.current++;
      iterationRef.current = 0;
      frameRef.current = requestAnimationFrame(scrambleText);
    } else {
      isAnimatingRef.current = false;
      mainTitleRef.current.textContent = finalText;
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              isAnimatingRef.current = true;
              iterationRef.current = 0;
              lengthRef.current = 1;
              scrambleText();
            }, 500);
          }
        });
      },
      {
        threshold: 0.3
      }
    );

    if (mainTitleRef.current) {
      mainTitleRef.current.textContent = "";
      observer.observe(mainTitleRef.current);
    }

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      observer.disconnect();
    };
  }, []);

  // 1초마다 남은 시간 업데이트
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  
  // 프로그래스바 업데이트 - 초기 시간을 타겟 날짜 기준으로 변경
  useEffect(() => {
    const now = new Date();
    const difference = targetDate - now;
    
    // 이미 종료된 경우 100% 표시
    if (difference <= 0) {
      setProgress(100);
      return;
    }
    
    // 전체 기간 (여기서는 예시로 30일을 사용, 필요에 따라 조정)
    const totalPeriod = 27 * 24 * 60 * 60 * 1000; // 30일을 밀리초로
    const elapsed = totalPeriod - difference;
    let newProgress = Math.min((elapsed / totalPeriod) * 100, 100);
    if (newProgress < 0) {
      newProgress = 0;
    }
    setProgress(newProgress);
  }, [timeLeft]);

  // 이전 시간과 현재 시간을 비교하여 변경된 단위만 애니메이션 적용
  useEffect(() => {
    const currentElements = document.querySelectorAll(".current");
    const nextElements = document.querySelectorAll(".next");

    // 각 단위별로 변경 여부 확인
    const shouldAnimate = {
      days: timeLeft.days !== prevTimeLeft.days,
      hours: timeLeft.hours !== prevTimeLeft.hours,
      minutes: timeLeft.minutes !== prevTimeLeft.minutes,
      seconds: timeLeft.seconds !== prevTimeLeft.seconds,
    };

    // 변경된 단위에 대해서만 애니메이션 적용
    Object.entries(shouldAnimate).forEach(([unit, shouldAnimate], index) => {
      if (shouldAnimate) {
        const currentEl = currentElements[index];
        const nextEl = nextElements[index];

        // 현재 숫자를 위로 올리면서 회전
        currentEl.style.transform = "translateY(-100%) rotateX(-90deg)";
        currentEl.style.opacity = "0";

        // 다음 숫자를 아래에서 위로 올리면서 회전
        nextEl.style.transform = "translateY(0) rotateX(0deg)";
        nextEl.style.opacity = "1";
        nextEl.classList.add("animate");

        // 애니메이션이 끝나면 클래스 제거
        setTimeout(() => {
          nextEl.classList.remove("animate");
        }, 300);
      }
    });

    // 애니메이션이 완료된 후에만 prevTimeLeft를 업데이트
    if (Object.values(shouldAnimate).some(Boolean)) {
      setTimeout(() => {
        setPrevTimeLeft(timeLeft);
      }, 300);
    }
  }, [timeLeft, prevTimeLeft]);

  //==========================================================================================

  //==========================================================================================
  // 1. 암호화폐 결제 
  const handleCryptoPurchase = async () => {
    // return;
    setError("");

    if (!isConnected) {
      await connectWallet();
    } else {
      setModalType("crypto");
      setShowModal(true);
    }
  };

  // 1-2. Solflare 지갑 연결 함수
  const connectWallet = async () => {
    // return; // 테스트용
    setError("");

    try {
      const solflare = new SolflareWalletAdapter();
      await solflare.connect();
      
      if (!solflare.connected) {
        throw new Error("Failed to connect Solflare wallet.");
      }

      setWallet(solflare);
      setIsConnected(true);
      setProvider(solflare);
    } catch (error) {
      console.error("Wallet connection failed:", error);
      setError(error.message);
    }
  };

  // 2. 트랜잭션 전송 함수
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setShowSuccess(false);

    try {
      // 입력 유효성 검사
      if (!amount || amount <= 0) {
        throw new Error("Please enter a valid amount");
      }
      if (amount < MIN_AMOUNT) {
        throw new Error(`The minimum purchase amount is ${MIN_AMOUNT} TITANIUM`);
      }

      // if (!email || !email.includes("@")) {
      //   throw new Error("Please enter a valid email address");
      // }

      // 실제 결제 API 호출 대신 시뮬레이션
      // await new Promise((resolve) => setTimeout(resolve, 2000));

      if (modalType === "card") {
        console.log("Card payment processing:", {
          amount,
          // email,
          total: calculateTotal(),
        });
      } else {
        console.log("Crypto payment processing:", {
          amount,
          // email,
          total: calculateTotal(),
        });
        purchaseWithUSDC();
      }
    } catch (error) {
      setError(error.message);
      console.error("Error occurred during payment processing:", error);
    }
  };

  // 잔액 확인 함수
  const checkBalance = async () => {
    const response = await fetch(API_URL + '/wallet/balance', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({  
        walletAddress: wallet.publicKey.toString()
      })
    });
    const balance = await response.json();
    console.log("balance", balance.balance);
    return balance.balance;
  };

  // USDC 토큰 구매 함수
  const purchaseWithUSDC = async () => {
    const balanceOld = await checkBalance();
    
    // return;
    if (isPurchasing) return; // 중복 방지
    setIsPurchasing(true);
    
    try {
      if (!isConnected) {
        await connectWallet();
        return;
      }

      if (!amount || amount <= 0) {
        throw new Error("Please enter a valid amount");
      }

      // if (!email || !email.includes("@")) {
      //   throw new Error("Please enter a valid email address");
      // }

      // 1. 트랜잭션 생성 요청
      const response = await fetch(API_URL + '/transaction/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          // email: email,
          walletAddress: wallet.publicKey.toString()
        })
      });

      if (!response.ok) {
        throw new Error('Server response error');
      }

      const result = await response.json();      
      if (!result.success) {
        throw new Error('Server response error');
      }
      const idx = result.idx;

      // 2. 트랜잭션 서명
      if (!result.transactionJSON || !result.transactionJSON.instructions || !result.transactionJSON.recentBlockhash) {
        throw new Error('Transaction data is missing');
      }

      // 새 트랜잭션 객체 생성
      const transaction = new Transaction();
      transaction.recentBlockhash = result.transactionJSON.recentBlockhash;
      transaction.feePayer = wallet.publicKey;

      // 서버에서 받은 명령어 정보로 트랜잭션 재구성
      for (const instData of result.transactionJSON.instructions) {
        // programId 변환
        const programId = new PublicKey(instData.programId);
        
        // keys 변환
        const keys = instData.keys.map(k => ({
          pubkey: new PublicKey(k.pubkey),
          isSigner: k.isSigner,
          isWritable: k.isWritable
        }));
        
        // data 변환
        const data = Buffer.from(instData.data);
        
        // 명령어 객체 생성 및 추가
        const instruction = {
          programId,
          keys,
          data
        };
        
        transaction.add(instruction);
      }

      // 트랜잭션 서명
      const signed = await wallet.signTransaction(transaction);
      const signedTransaction = signed.serialize().toString('base64');
      
      // 3. 서명된 트랜잭션 전송
      const sendResponse = await fetch(API_URL + '/transaction/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          // email: email,
          walletAddress: wallet.publicKey.toString(),
          signedTransaction: signedTransaction,
          idx: idx
        })
      });

      if (!sendResponse.ok) {
        throw new Error('transaction send failed');
      }

      const sendResult = await sendResponse.json();      
      if (sendResult.success) {
        console.log("buy success");
        setShowSuccess(true);
        setTimeout(() => {
          handleCloseModal();
          setShowSuccess(false);
        }, 3000);
      } else {
        throw new Error(sendResult.message || 'buy failed');
      }

    } catch (error) {
      const balanceNew = await checkBalance();
      if (balanceNew === parseFloat(balanceOld)+parseFloat(amount)) {
        console.log("buy success");
        setShowSuccess(true);
        setTimeout(() => {
          handleCloseModal();
          setShowSuccess(false);
        }, 3000);
        return;
      }

      console.error("buy failed:", error);
      setError(error.message);
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleCardPurchase = () => {
    setModalType("card");
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setAmount("");
    setEmail("");
  };


  const calculateTotal = () => {
    return (amount * TITANIUM_PRICE).toFixed(4);
  };

  return (
    <HeroContainer id="main-1" ref={sectionRef}>
      <HeroWrap>
        <TextContent>
          <TitleContainer>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <BusinessTitle>The First Business</BusinessTitle>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <MainTitle ref={mainTitleRef}></MainTitle>
            </motion.div>
          </TitleContainer>

          <InfoContainer>
            <TopWrapper>
              <RaisedContainer>
                <PurchaseTitle>
                  Purchase <span>$TITANIUM</span> tokens
                </PurchaseTitle>
              </RaisedContainer>

              <CountdownContainer>
                <CountdownItem>
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                  >
                    <CountdownNumber>
                      <NumberWrapper className="current">
                        {String(prevTimeLeft.days).padStart(2, "0")}
                      </NumberWrapper>
                      <NumberWrapper className="next">
                        {String(timeLeft.days).padStart(2, "0")}
                      </NumberWrapper>
                    </CountdownNumber>
                  </motion.div>
                  <CountdownLabel>days</CountdownLabel>
                </CountdownItem>
                <CountdownItem>
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                  >
                    <CountdownNumber>
                      <NumberWrapper className="current">
                        {String(prevTimeLeft.hours).padStart(2, "0")}
                      </NumberWrapper>
                      <NumberWrapper className="next">
                        {String(timeLeft.hours).padStart(2, "0")}
                      </NumberWrapper>
                    </CountdownNumber>
                  </motion.div>
                  <CountdownLabel>hours</CountdownLabel>
                </CountdownItem>
                <CountdownItem>
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                  >
                    <CountdownNumber>
                      <NumberWrapper className="current">
                        {String(prevTimeLeft.minutes).padStart(2, "0")}
                      </NumberWrapper>
                      <NumberWrapper className="next">
                        {String(timeLeft.minutes).padStart(2, "0")}
                      </NumberWrapper>
                    </CountdownNumber>
                  </motion.div>
                  <CountdownLabel>minutes</CountdownLabel>
                </CountdownItem>
                <CountdownItem>
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                  >
                    <CountdownNumber>
                      <NumberWrapper className="current">
                        {String(prevTimeLeft.seconds).padStart(2, "0")}
                      </NumberWrapper>
                      <NumberWrapper className="next">
                        {String(timeLeft.seconds).padStart(2, "0")}
                      </NumberWrapper>
                    </CountdownNumber>
                  </motion.div>
                  <CountdownLabel>seconds</CountdownLabel>
                </CountdownItem>
              </CountdownContainer>

              <PriceText>1 TITANIUM = $ {TITANIUM_PRICE}</PriceText>

              <TimeNotice>
                <TimeProgressBar>
                  <TimeNoticeText>
                    Time remaining before price increases
                  </TimeNoticeText>
                  <TimeProgressFill style={{ width: `${progress}%` }} />
                </TimeProgressBar>
              </TimeNotice>
            </TopWrapper>

            <BottomWrapper>
              <ButtonsContainer>
                {/* <PurchaseButton onClick={handleCardPurchase}>
                  <ButtonIcon>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="1"
                        y="4"
                        width="22"
                        height="16"
                        rx="2"
                        ry="2"
                      ></rect>
                      <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                  </ButtonIcon>
                  Buy with a card
                </PurchaseButton> */}
                <PurchaseButton onClick={handleCryptoPurchase}>
                  <ButtonIcon>
                    <img 
                      src="https://framerusercontent.com/images/S5LL7zoiaBmOI6TXerm07Kf4aI.svg" 
                      alt="Button Icon" 
                      width={30}
                      height={30}
                    />
                  </ButtonIcon>
                  {isConnected ? "Buy in cryptocurrency" : "Connect Wallet"}
                </PurchaseButton>
              </ButtonsContainer>
            </BottomWrapper>
          </InfoContainer>
        </TextContent>
        <ImageContainer>
          <ImageWrapper>
            <HeroImage
              src="https://framerusercontent.com/images/L41kv46JN1vgok5JFf7XGpMjjk.png"
              alt="Hero Image"
              decoding="async"
              sizes="(min-width: 1440px) max(min(100vw - 80px, 1400px) / 2, 1px), (min-width: 810px) and (max-width: 1439px) max(min(100vw - 80px, 1400px) / 2, 1px), (max-width: 809px) calc((min(100vw - 40px, 1400px) - 40px) / 1.342)"
              srcSet="https://framerusercontent.com/images/L41kv46JN1vgok5JFf7XGpMjjk.png?scale-down-to=512 512w,https://framerusercontent.com/images/L41kv46JN1vgok5JFf7XGpMjjk.png?scale-down-to=1024 1024w,https://framerusercontent.com/images/L41kv46JN1vgok5JFf7XGpMjjk.png 1417w"
            />
          </ImageWrapper>
        </ImageContainer>
      </HeroWrap>
      <MediaContainer>
        <MediaTitle>Featured in the Media</MediaTitle>
        <LogoSlider>
          <LogoContainer>
            {duplicatedLogos.map((logo, index) => (
              <LogoImage key={index} src={logo.src} alt={logo.alt} />
            ))}
          </LogoContainer>
        </LogoSlider>
      </MediaContainer>
      <BackgroundOverlay />

      {showModal && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseModal}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {showSuccess ? (
              <SuccessMessage
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <SuccessIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </SuccessIcon>
                <SuccessText>Payment Successful!</SuccessText>
                <SuccessDescription>
                  Your purchase of {amount} TITANIUM tokens has been processed
                  successfully.
                </SuccessDescription>
              </SuccessMessage>
            ) : (
              <>
                <CloseButton onClick={handleCloseModal}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </CloseButton>

                <ModalTitle>
                  {modalType === "card"
                    ? "Buy with Card"
                    : "Buy with Cryptocurrency"}
                </ModalTitle>

                <form onSubmit={handleSubmit}>
                  {error && (
                    <ErrorMessage>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      {error}
                    </ErrorMessage>
                  )}

                  <InputGroup>
                    <InputLabel>Amount (TITANIUM)</InputLabel>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter amount"
                      required
                      min="1"
                    />
                  </InputGroup>

                  <PriceInfo>
                    <PriceRow>
                      <span>Price per TITANIUM</span>
                      <span>${TITANIUM_PRICE}</span>
                    </PriceRow>
                    <PriceRow>
                      <span>Amount</span>
                      <span>{amount || 0} TITANIUM</span>
                    </PriceRow>
                    <PriceRow className="total">
                      <span>Total</span>
                      <span>{calculateTotal()} USDC</span>
                    </PriceRow>
                  </PriceInfo>

                  {modalType === "card" && (
                    <PaymentMethod>
                      <PaymentOption
                        type="button"
                        active={paymentMethod === "card"}
                        onClick={() => setPaymentMethod("card")}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="1"
                            y="4"
                            width="22"
                            height="16"
                            rx="2"
                            ry="2"
                          ></rect>
                          <line x1="1" y1="10" x2="23" y2="10"></line>
                        </svg>
                        Credit Card
                      </PaymentOption>
                      <PaymentOption
                        type="button"
                        active={paymentMethod === "bank"}
                        onClick={() => setPaymentMethod("bank")}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="2"
                            y="7"
                            width="20"
                            height="14"
                            rx="2"
                            ry="2"
                          ></rect>
                          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                        </svg>
                        Bank Transfer
                      </PaymentOption>
                    </PaymentMethod>
                  )}

                  {/* <InputGroup>
                    <InputLabel>Email</InputLabel>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                    />
                  </InputGroup> */}

                  <SubmitButton type="submit" disabled={isPurchasing}>
                    {modalType === "card"
                      ? "Proceed to Payment"
                      : "Buy"}
                  </SubmitButton>
                </form>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </HeroContainer>
  );
}

export default HeroSection;
